/**
 * ProductChangeItem Component
 * Displays a single product with price/stock changes
 * UI matches checkout lama exactly
 */

import {
  Box,
  HStack,
  Icon,
  Image,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';
import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import { Button } from '@diamond/shared/ui';
import {
  buyerCurrencyFormatter,
  numberWithSeparator,
} from '@diamond/shared/utils';
import { ArrowForward } from '@mui/icons-material';
import { dinero, toDecimal } from 'dinero.js';
import Link from 'next/link';

import type { ProductChangeItemProps } from './types';

export function ProductChangeItem({
  item,
  index,
  warningType,
}: ProductChangeItemProps) {
  const stock = Number(
    toDecimal(
      dinero({
        amount: Number(item.stock),
        currency: STOCK_UI,
      })
    )
  );

  const stockDiff = Number(
    toDecimal(
      dinero({
        amount: Number(item.stock_diff || 0),
        currency: STOCK_UI,
      })
    )
  );

  const stockDiffStr = numberWithSeparator(stockDiff);
  const isOutOfStock = stockDiffStr === '0';

  return (
    <Stack
      direction={{ base: 'column', md: 'row' }}
      key={`produk-${index}`}
      borderBottomWidth="1px"
      borderBottomColor="gray.33"
      pb="4"
      pt={index !== 0 ? '4' : '0'}
    >
      {/* Product Image & Info */}
      <HStack align="start">
        <Image
          src={item.product_image}
          alt={item.title}
          boxSize="80px"
          fallbackSrc="/images/product_placeholder.png"
          objectFit="scale-down"
        />
        <Box>
          <Text fontWeight="bold" textTransform="capitalize">
            {item.brand_desc}
          </Text>
          <Text>{item.title}</Text>
          <Text color="gray">{item.item_code}</Text>
        </Box>
      </HStack>

      <Spacer display={{ base: 'none', md: 'block' }} />

      {/* Stock & Price Changes */}
      <Box
        minWidth="250px"
        display={
          warningType === 'price_stock_changes' || warningType === 'no_product'
            ? 'flex'
            : 'none'
        }
        alignSelf="center"
        flexDirection="column"
        gap="10px"
      >
        {/* Stock Change */}
        {item.is_stock_diff && (
          <Box>
            <Text color="gray.75">Perubahan Stok</Text>
            <HStack>
              <Text color="gray.75">{numberWithSeparator(stock)}</Text>
              <Icon as={ArrowForward} />
              <Text
                fontWeight="bold"
                textColor={isOutOfStock ? 'red' : 'black'}
              >
                {stockDiffStr}
              </Text>
            </HStack>
          </Box>
        )}

        {/* Price Change */}
        {item.is_price_diff && (
          <Box
            display={warningType === 'price_stock_changes' ? 'block' : 'none'}
          >
            <Text color="gray.75">Perubahan Harga Satuan</Text>
            <HStack justifyContent="space-between">
              <Text color="gray.75">{buyerCurrencyFormatter(item.price)}</Text>
              <Icon as={ArrowForward} />
              <Text fontWeight="bold">
                {buyerCurrencyFormatter(item.price_diff || item.price)}
              </Text>
            </HStack>
          </Box>
        )}

        {/* Cari Produk Serupa Button */}
        {item.is_stock_diff && isOutOfStock && (
          <Link href={`/product/${item.id}/suggestions?cat1=&page=1`}>
            <Button
              variant={
                warningType === 'price_stock_changes' ? 'outline' : 'solid'
              }
              size="sm"
              fontWeight="normal"
            >
              <HStack spacing="10px">
                <Text>Cari Produk Serupa</Text>
                <Icon as={ArrowForward} />
              </HStack>
            </Button>
          </Link>
        )}
      </Box>
    </Stack>
  );
}
